import React, { useState, useEffect } from 'react';
import { X, Download, AlertCircle, Loader } from 'lucide-react';
import { fileService } from '../services/fileService';

function FilePreviewModal({ isOpen, onClose, file }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && file) {
      loadPreview();
    }
    
    return () => {
      // Cleanup blob URL when modal closes
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [isOpen, file]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Download the file as blob
      const response = await fileService.downloadFileBlob(file.id);
      const blob = new Blob([response], { type: file.mime_type });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      setError('Failed to load preview: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await fileService.downloadFile(file.id, file.original_filename);
    } catch (err) {
      setError('Failed to download: ' + err.message);
    }
  };

  const isImage = file?.mime_type?.startsWith('image/');
  const isPDF = file?.mime_type === 'application/pdf';
  const canPreview = isImage || isPDF;
  
  // Get file type name
  const getFileTypeName = () => {
    if (isImage) return 'Image';
    if (isPDF) return 'PDF';
    if (file?.mime_type?.includes('word') || file?.original_filename?.endsWith('.docx')) return 'Word Document';
    if (file?.mime_type?.includes('excel') || file?.original_filename?.endsWith('.xlsx')) return 'Excel Spreadsheet';
    if (file?.mime_type?.includes('powerpoint') || file?.original_filename?.endsWith('.pptx')) return 'PowerPoint Presentation';
    return 'File';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {file?.original_filename}
            </h2>
            <p className="text-sm text-gray-500">
              {file?.mime_type}
            </p>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <Loader className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full">
              <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-start gap-2 max-w-md">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {!loading && !error && canPreview && previewUrl && (
            <>
              {isImage && (
                <div className="flex items-center justify-center min-h-full">
                  <img
                    src={previewUrl}
                    alt={file.original_filename}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  />
                </div>
              )}

              {isPDF && (
                <iframe
                  src={previewUrl}
                  className="w-full h-full min-h-[600px] rounded-lg shadow-lg bg-white"
                  title={file.original_filename}
                />
              )}
            </>
          )}

          {!loading && !error && !canPreview && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Preview not available for {getFileTypeName()}
                </h3>
                <p className="text-gray-500 mb-4">
                  {getFileTypeName()} files cannot be previewed in the browser. Download to view in the appropriate application.
                </p>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
                >
                  <Download className="w-4 h-4" />
                  Download File
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FilePreviewModal;
